import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABEL = { entry: "Entrata", exit: "Uscita", return: "Reso", transfer: "Trasferimento", scarico_commessa: "Scarico Commessa", reso_fornitore: "Reso a Fornitore", recupero_commessa: "Recupero Commessa" };

/**
 * Encapsulates the Movements submit flow:
 *   - validates required fields client-side (dup of server rules)
 *   - decides between POST /movements and POST /movements/bulk
 *     (bulk is used whenever multiple products are grouped OR when the
 *     operator is fulfilling a transfer order — the latter so the backend
 *     closes the ODT in the same transaction)
 *   - fires the appropriate toast + resets the form on success
 *   - returns the created movement(s) via `onSuccess` so the caller can
 *     open the signature modal on the right lead movement
 *
 * The hook is intentionally dumb about state ownership: the caller passes
 * the full form state via `getState()` so React has a single source of truth.
 */
export default function useMovementSubmit({ getState, resetForm, reloadList, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const s = getState();
    const {
      type, productId, fromWh, toWh, serialsText, scannedList, scannerMode,
      quantity, reference, notes, customerId, commessaId, commessaNewMode,
      commessaNameNew, bulkItems, transferOrderId, selectedProduct,
      entrySupplierId, entryUnitPrice, resoSupplierId, movementDate,
    } = s;

    const needsFrom = ["exit", "transfer", "scarico_commessa", "reso_fornitore"].includes(type);
    const needsTo = ["entry", "return", "transfer", "recupero_commessa"].includes(type);
    const isBulk = selectedProduct && selectedProduct.is_serialized === false;

    if (!productId) { toast.error("Seleziona un prodotto"); return; }
    if (needsFrom && !fromWh) { toast.error("Seleziona magazzino di origine"); return; }
    if (needsTo && !toWh) { toast.error("Seleziona magazzino di destinazione"); return; }

    const payload = {
      type, product_id: productId,
      from_warehouse_id: needsFrom ? fromWh : null,
      to_warehouse_id: needsTo ? toWh : null,
      reference: reference || "",
      notes: notes || "",
    };
    if (movementDate) {
      const d = new Date(movementDate);
      if (Number.isNaN(d.getTime())) { toast.error("Data movimento non valida"); return; }
      if (d.getTime() > Date.now() + 60000) { toast.error("La data del movimento non può essere nel futuro"); return; }
      payload.movement_date = d.toISOString();
    }

    if (type === "exit") {
      if (!customerId) { toast.error("Seleziona un Cliente destinatario"); return; }
      payload.customer_id = customerId;
    } else if (type === "return") {
      if (!customerId) { toast.error("Seleziona il Cliente che restituisce"); return; }
      payload.customer_id = customerId;
    } else if (type === "reso_fornitore") {
      if (!resoSupplierId) { toast.error("Seleziona il Fornitore a cui rendere il materiale"); return; }
      payload.supplier_id = resoSupplierId;
    } else if (type === "recupero_commessa") {
      if (!commessaId) { toast.error("Seleziona la Commessa da cui recuperare"); return; }
      payload.commessa_id = commessaId;
    } else if (type === "scarico_commessa") {
      // Iter 47: admin/gestore (desktop) may scarico from physical warehouses too.
      // Operator restriction is enforced by the backend ACL + disabled select.
      if (commessaNewMode) {
        if (!commessaNameNew.trim()) { toast.error("Inserisci il nome della nuova commessa"); return; }
        payload.commessa_name_new = commessaNameNew.trim();
      } else {
        if (!commessaId) { toast.error("Seleziona una Commessa destinataria"); return; }
        payload.commessa_id = commessaId;
      }
    }

    let successLabel = "";
    if (isBulk) {
      const qty = parseInt(quantity, 10);
      if (!qty || qty <= 0) { toast.error("Inserisci una quantità valida"); return; }
      payload.quantity = qty;
      payload.serial_numbers = [];
      successLabel = `${qty} pz`;
      // Bulk entry MUST declare the supplier so the lot is traceable (FIFO).
      if (type === "entry") {
        if (!entrySupplierId) { toast.error("Seleziona il fornitore del lotto"); return; }
        payload.supplier_id = entrySupplierId;
        const price = parseFloat(entryUnitPrice);
        if (!Number.isNaN(price) && price >= 0) payload.unit_price = price;
      }
    } else {
      const serialsFromText = serialsText.split(/[\s,;\n]+/).map((x) => x.trim()).filter(Boolean);
      const serials = scannerMode ? scannedList : serialsFromText;
      if (!serials.length) { toast.error("Inserisci almeno un seriale"); return; }
      payload.serial_numbers = serials;
      successLabel = `${serials.length} pz`;
    }

    setSubmitting(true);
    try {
      // Multi-product path OR ODT fulfilment (always uses bulk to carry the
      // transfer_order_id and close the ODT server-side).
      if (bulkItems.length > 0 || transferOrderId) {
        const items = [
          ...bulkItems,
          {
            product_id: productId,
            quantity: isBulk ? (parseInt(quantity, 10) || 1) : payload.serial_numbers.length,
            serial_numbers: isBulk ? null : payload.serial_numbers,
            supplier_id: type === "entry" ? (payload.supplier_id || null) : null,
            unit_price: payload.unit_price ?? null,
          },
        ];
        const bulkPayload = {
          type,
          from_warehouse_id: payload.from_warehouse_id,
          to_warehouse_id: payload.to_warehouse_id,
          customer_id: payload.customer_id || null,
          commessa_id: payload.commessa_id || null,
          supplier_dest_id: type === "reso_fornitore" ? resoSupplierId : null,
          movement_date: payload.movement_date || null,
          reference: payload.reference || "",
          notes: payload.notes || "",
          items,
          transfer_order_id: transferOrderId || null,
        };
        const { data } = await api.post("/movements/bulk", bulkPayload);
        toast.success(
          transferOrderId
            ? `ODT evaso · ${data.created.length} movimenti creati`
            : `${TYPE_LABEL[type]} registrata (${data.created.length} prodotti raggruppati)`
        );
        if (data.errors && data.errors.length) toast.warning(data.errors.join("\n"));
        resetForm({ fullReset: true });
        await reloadList();
        const first = data.created?.[0];
        if (first && ["exit", "transfer", "scarico_commessa", "reso_fornitore"].includes(type)) {
          onSuccess?.({ bulk: true, lead: first, type });
        }
        return;
      }

      const { data: createdMov } = await api.post("/movements", payload);
      toast.success(`${TYPE_LABEL[type]} registrata (${successLabel})`);
      resetForm({ fullReset: false });
      await reloadList();
      if (createdMov?.id) {
        onSuccess?.({ bulk: false, lead: createdMov, type: createdMov.type });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return { submit, submitting };
}
